import type React from "react";
import {List, Root, Title} from "./PickedImageOutcome.styles";

export type PickedImageOutcomeProps = {
	/** The block's heading, from the CALLER's catalog. */
	title: string;
	/** One `<li>` per thing the pick could not do — again the caller's copy, and its own keys. */
	children: React.ReactNode;
};

/**
 * What a multi-file image pick could NOT add — shared by the sponsor creative picker and the account
 * page-theme picker (ui#752).
 *
 * The decision this settles: the two surfaces rendered structurally identical markup over identical
 * styles, written separately (ui#664/#686). With no UI-component tests in this repo, a divergence
 * between them could only ever surface as an e2e failure on whichever surface happened to change — so
 * the copies could drift silently. One component removes that.
 *
 * It deliberately owns only the SHAPE: the alert wrapper, its heading and its list. Each surface passes
 * its own title and its own `<li>` lines from its own catalog, because what could not be added differs
 * per picker — the theme surface also reports files still uploading, which the sponsor page has no
 * notion of. Sharing the copy as well would have forced one vocabulary onto two features.
 *
 * `role="alert"` is on the wrapper so the outcome reaches someone not looking at the row — the file
 * dialog took their attention away from it. **Callers must key this by their report id**: two picks can
 * produce identical text, re-rendering the same characters mutates no DOM, and an alert that does not
 * change is one screen readers stay silent about. `key` cannot be read from inside a component, which
 * is why it is the caller's job and is stated here rather than assumed.
 *
 * Lives in `components/` rather than beside `core/images/pickedImageBatch.ts`, the module both pickers
 * already share: `src/core/` is React-free by the epic #26 mandate (no `.tsx`, no React import anywhere
 * under it), so a component there would have been the first breach of it.
 */
export function PickedImageOutcome({title, children}: PickedImageOutcomeProps) {
	return (
		<Root role="alert">
			<Title>{title}</Title>
			<List>{children}</List>
		</Root>
	);
}
